import React, { useState } from "react";
import axios from "axios";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";

// Types matching the backend DTOs
interface ChaletInventoryDTO {
  totalBigChalets: number;
  totalSmallChalets: number;
  totalChalets: number;
}

interface AvailabilitySummaryDTO {
  bigChaletsBooked: number;
  bigChaletsAvailable: number;
  smallChaletsBooked: number;
  smallChaletsAvailable: number;
}

interface ChaletStatusDTO {
  chaletId: number;
  chaletType: string;
  isBooked: boolean;
  bookingId: number | null;
  customerName: string | null;
  companyName: string | null;
  bookingDates: string | null;
  nights: number | null;
  status: string | null;
}

interface BookingOverviewDTO {
  bookingId: number;
  orderNumber: string;
  customerName: string;
  companyName: string | null;
  chaletId: number;
  chaletType: string;
  arrivalDate: string;
  departureDate: string;
  nights: number;
  packageName: string;
  status: string;
  isBlocked: boolean;
}

interface ManagerChaletOverviewDTO {
  dateRange: string;
  inventory: ChaletInventoryDTO;
  availabilitySummary: AvailabilitySummaryDTO;
  chaletDetails: ChaletStatusDTO[];
  bookings: BookingOverviewDTO[];
}

const DetailedChaletBookings: React.FC = () => {
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [data, setData] = useState<ManagerChaletOverviewDTO | null>(null);

  const handleDateChange = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
  };

  const fetchDetailedBookings = async () => {
    if (!startDate || !endDate) {
      setError("Please select both start and end dates.");
      return;
    }

    setLoading(true);
    setError("");
    setData(null);

    try {
      const formattedStart = format(startDate, "yyyy-MM-dd");
      const formattedEnd = format(endDate, "yyyy-MM-dd");

      const response = await axios.get<ManagerChaletOverviewDTO>(
        `${process.env.REACT_APP_API_URL}/PackageBooking/detailed-chalet-bookings`,
        {
          params: {
            startDate: formattedStart,
            endDate: formattedEnd,
          },
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      );

      setData(response.data);
    } catch (err: any) {
      console.error("Error fetching detailed bookings:", err);
      if (err.response?.status === 401) {
        setError("Unauthorized. Please log in again.");
      } else if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError("Failed to fetch booking details. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mt-4 mb-5">
      <div className="card">
        <div className="card-header bg-primary text-white">
          <h3 className="mb-0">
            <i className="fas fa-search me-2"></i>
            Detailed Chalet Booking Lookup
          </h3>
          <small>See EXACTLY which chalets are booked and by whom for any date range</small>
        </div>

        <div className="card-body">
          {/* Date Range Selector */}
          <div className="row mb-4">
            <div className="col-md-6">
              <label className="form-label fw-bold">Select Date Range:</label>
              <DatePicker
                selected={startDate}
                onChange={handleDateChange}
                startDate={startDate ?? undefined}
                endDate={endDate ?? undefined}
                selectsRange
                className="form-control"
                placeholderText="Click to select dates"
                dateFormat="yyyy-MM-dd"
                calendarStartDay={1}
              />
            </div>
            <div className="col-md-6 d-flex align-items-end">
              <button
                className="btn btn-primary"
                onClick={fetchDetailedBookings}
                disabled={loading || !startDate || !endDate}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    Loading...
                  </>
                ) : (
                  <>
                    <i className="fas fa-search me-2"></i>
                    Check Availability
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="alert alert-danger" role="alert">
              <i className="fas fa-exclamation-triangle me-2"></i>
              {error}
            </div>
          )}

          {/* Results */}
          {data && (
            <>
              {/* CRITICAL WARNING: Bookings without chalet assignment */}
              {data.bookings.filter(b => !b.chaletId || b.chaletId === 0).length > 0 && (
                <div className="alert alert-danger mb-4" role="alert">
                  <h5 className="alert-heading">
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    ⚠️ CRITICAL: Bookings Without Chalet Assignment Detected!
                  </h5>
                  <p className="mb-2">
                    <strong>{data.bookings.filter(b => !b.chaletId || b.chaletId === 0).length} booking(s)</strong> in this date range 
                    do NOT have a chalet assigned. These bookings are causing availability calculation issues.
                  </p>
                  <hr />
                  <p className="mb-0">
                    <strong>Action Required:</strong> These bookings need to be edited in the database or admin panel 
                    to assign a specific chalet. Look for rows highlighted in RED in the booking table below.
                  </p>
                </div>
              )}

              {/* Summary Header */}
              <div className="alert alert-info mb-4">
                <h5 className="mb-2">
                  <i className="fas fa-calendar-alt me-2"></i>
                  Date Range: {data.dateRange}
                </h5>
              </div>

              {/* Inventory & Availability Summary */}
              <div className="row mb-4">
                {/* Inventory Card */}
                <div className="col-md-6">
                  <div className="card border-secondary">
                    <div className="card-header bg-secondary text-white">
                      <i className="fas fa-home me-2"></i>
                      Total Chalet Inventory
                    </div>
                    <div className="card-body">
                      <table className="table table-sm mb-0">
                        <tbody>
                          <tr>
                            <td>Big Chalets (4-bed):</td>
                            <td className="fw-bold text-end">{data.inventory.totalBigChalets}</td>
                          </tr>
                          <tr>
                            <td>Small Chalets (2-bed):</td>
                            <td className="fw-bold text-end">{data.inventory.totalSmallChalets}</td>
                          </tr>
                          <tr className="table-dark">
                            <td>Total:</td>
                            <td className="fw-bold text-end">{data.inventory.totalChalets}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                {/* Availability Summary Card */}
                <div className="col-md-6">
                  <div className="card border-info">
                    <div className="card-header bg-info text-white">
                      <i className="fas fa-chart-pie me-2"></i>
                      Availability Summary
                    </div>
                    <div className="card-body">
                      <table className="table table-sm mb-0">
                        <thead>
                          <tr>
                            <th>Type</th>
                            <th className="text-center text-danger">Booked</th>
                            <th className="text-center text-success">Available</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td>Big (4-bed)</td>
                            <td className="text-center">
                              <span className="badge bg-danger">{data.availabilitySummary.bigChaletsBooked}</span>
                            </td>
                            <td className="text-center">
                              <span className="badge bg-success">{data.availabilitySummary.bigChaletsAvailable}</span>
                            </td>
                          </tr>
                          <tr>
                            <td>Small (2-bed)</td>
                            <td className="text-center">
                              <span className="badge bg-danger">{data.availabilitySummary.smallChaletsBooked}</span>
                            </td>
                            <td className="text-center">
                              <span className="badge bg-success">{data.availabilitySummary.smallChaletsAvailable}</span>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>

              {/* Chalet Status Grid */}
              <div className="card mb-4">
                <div className="card-header bg-dark text-white">
                  <i className="fas fa-th me-2"></i>
                  Individual Chalet Status
                </div>
                <div className="card-body">
                  <div className="row">
                    {data.chaletDetails.map((chalet) => (
                      <div key={chalet.chaletId} className="col-md-4 col-lg-3 mb-3">
                        <div
                          className={`card h-100 ${
                            chalet.isBooked ? "border-danger" : "border-success"
                          }`}
                        >
                          <div
                            className={`card-header ${
                              chalet.isBooked ? "bg-danger" : "bg-success"
                            } text-white py-2`}
                          >
                            <strong>Chalet #{chalet.chaletId}</strong>
                            <br />
                            <small>{chalet.chaletType}</small>
                          </div>
                          <div className="card-body py-2">
                            {chalet.isBooked ? (
                              <>
                                <p className="mb-1">
                                  <i className="fas fa-user me-1 text-muted"></i>
                                  <strong>{chalet.customerName}</strong>
                                </p>
                                {chalet.companyName && (
                                  <p className="mb-1 small text-muted">
                                    <i className="fas fa-building me-1"></i>
                                    {chalet.companyName}
                                  </p>
                                )}
                                <p className="mb-1 small">
                                  <i className="fas fa-calendar me-1 text-muted"></i>
                                  {chalet.bookingDates}
                                </p>
                                <p className="mb-0 small">
                                  <i className="fas fa-moon me-1 text-muted"></i>
                                  {chalet.nights} nights
                                </p>
                              </>
                            ) : (
                              <p className="mb-0 text-success">
                                <i className="fas fa-check-circle me-1"></i>
                                Available
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Booking Details Table */}
              {data.bookings.length > 0 && (
                <div className="card">
                  <div className="card-header bg-warning text-dark">
                    <i className="fas fa-list me-2"></i>
                    All Bookings Overlapping This Period ({data.bookings.length})
                    {data.bookings.filter(b => !b.chaletId || b.chaletId === 0).length > 0 && (
                      <span className="badge bg-danger ms-2">
                        ⚠️ {data.bookings.filter(b => !b.chaletId || b.chaletId === 0).length} booking(s) WITHOUT chalet assignment!
                      </span>
                    )}
                  </div>
                  <div className="card-body p-0">
                    <div className="table-responsive">
                      <table className="table table-striped table-hover mb-0">
                        <thead className="table-dark">
                          <tr>
                            <th>Booking ID</th>
                            <th>Order #</th>
                            <th>Customer</th>
                            <th>Company</th>
                            <th>Chalet</th>
                            <th>Arrival</th>
                            <th>Departure</th>
                            <th>Nights</th>
                            <th>Package</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.bookings.map((booking) => {
                            const hasNoChaletAssigned = !booking.chaletId || booking.chaletId === 0;
                            return (
                            <tr key={booking.bookingId} className={hasNoChaletAssigned ? "table-danger" : ""}>
                              <td>{booking.bookingId}</td>
                              <td>
                                <code>{booking.orderNumber}</code>
                              </td>
                              <td>{booking.customerName}</td>
                              <td>{booking.companyName || "-"}</td>
                              <td>
                                {hasNoChaletAssigned ? (
                                  <span className="badge bg-danger">
                                    ⚠️ NO CHALET ASSIGNED
                                  </span>
                                ) : (
                                  <span className="badge bg-secondary">
                                    #{booking.chaletId} ({booking.chaletType})
                                  </span>
                                )}
                              </td>
                              <td>{booking.arrivalDate}</td>
                              <td>{booking.departureDate}</td>
                              <td>{booking.nights}</td>
                              <td>{booking.packageName || "-"}</td>
                              <td>
                                <span
                                  className={`badge ${
                                    hasNoChaletAssigned
                                      ? "bg-danger"
                                      : booking.isBlocked
                                      ? "bg-dark"
                                      : booking.status === "Confirmed"
                                      ? "bg-success"
                                      : "bg-warning"
                                  }`}
                                >
                                  {hasNoChaletAssigned ? "⚠️ NEEDS FIX" : booking.isBlocked ? "Blocked" : booking.status}
                                </span>
                              </td>
                            </tr>
                          );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* Proof Statement */}
              <div className="alert alert-secondary mt-4" role="alert">
                <h6 className="alert-heading">
                  <i className="fas fa-info-circle me-2"></i>
                  What This Shows:
                </h6>
                <p className="mb-2">
                  This is a <strong>real-time view</strong> of all chalet bookings that overlap with your selected dates.
                  Each chalet can only be booked <strong>once per date range</strong>.
                </p>
                <hr />
                <p className="mb-0 small">
                  <strong>If all chalets show as "Booked":</strong> The system is working correctly - there simply
                  are no available chalets for those dates. The customers listed above have already reserved them.
                </p>
              </div>
            </>
          )}

          {/* Initial State */}
          {!data && !loading && !error && (
            <div className="text-center text-muted py-5">
              <i className="fas fa-calendar-check fa-3x mb-3"></i>
              <p>Select a date range and click "Check Availability" to see detailed booking information.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DetailedChaletBookings;
